/* eslint-disable prettier/prettier */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { showToast } from './toastSlice'
import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import {
  fetchCampaignMetaDataUrl,
  fetchCampaignUserCouponsUrl,
  fetchCampaignUserDataUrl,
  fetchProductsUrl,
  uploadFileUrl,
} from 'src/services/urls'

const initialState = {
  loading: false,
  data: { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 },
  docs: [],
  error: '',
  pageSize: 10,
  activePage: 0,
  allCampaignTypes: {},
  productList: [],
  couponCodes: [],
  couponCodesLoading: false,
}

export const fetchAllCampaignUserData = createAsyncThunk(
  'campaignUserData/fetchAllCampaignUserData',
  async (data, { dispatch, getState }) => {
    try {
      let query = '?'
      for (let key in data) {
        query += `${key}=${data[key]}&`
      }

      query = query.slice(0, -1)
      const url = fetchCampaignUserDataUrl(query)
      const response = await makeRequest(url)
      return { ...response.data.data }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const fetchCampaignUserCoupons = createAsyncThunk(
  'campaignUserData/fetchCampaignUserCoupons',
  async (data, { dispatch, getState }) => {
    try {
      let query = '?id=' + data
      const url = fetchCampaignUserCouponsUrl(query)
      const response = await makeRequest(url)

      return response.data.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const fetchCampaignTypes = createAsyncThunk(
  'campaignUserData/fetchCampaignTypes',
  async (data, { dispatch, getState }) => {
    try {
      const url = fetchCampaignMetaDataUrl('?types=true')
      const response = await makeRequest(url)
      return { ...response.data.data }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const fetchProducts = createAsyncThunk(
  'campaignUserData/fetchProducts',
  async (data, { dispatch, getState }) => {
    try {
      const url = fetchProductsUrl()
      const response = await makeRequest(url)
      return response.data.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)
export const uploadFile = createAsyncThunk(
  'files/uploadFile',
  async (data, { dispatch, getState }) => {
    try {
      const formData = new FormData()
      formData.append('file', data.file)
      const response = await makeRequest(uploadFileUrl(), 'POST', formData)
      const { displayMessage, status } = response.data
      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }
    } catch (e) {
      if (e?.response?.data?.error || e?.response?.data?.error === null) {
        if (e?.response?.data?.error === null) {
          dispatch(
            showToast({ title: 'Error', message: e.response.data.displayMessage, color: 'danger' }),
          )
          return
        }
        if (Object.keys(e?.response?.data?.error).length === 0) {
          dispatch(
            showToast({ title: 'Error', message: e.response.data.displayMessage, color: 'danger' }),
          )
          return
        }
      }
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

const campaignUserDataSlice = createSlice({
  name: 'campaignUserData',
  initialState,
  reducers: {
    resetDocs: (state, action) => {
      state.docs = []
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setActivePage: (state, action) => {
      state.activePage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setCouponCodesLoading: (state, action) => {
      state.couponCodesLoading = action.payload
    },
    resetCouponCodes: (state, action) => {
      state.couponCodes = []
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllCampaignUserData.pending, (state, action) => {})
    builder.addCase(fetchAllCampaignUserData.fulfilled, (state, action) => {
      state.data = action.payload
      let trackDocs = new Map()
      state.docs.forEach((d) => {
        if (!trackDocs.get(d._id)) trackDocs.set(d._id, d._id)
      })
      let newDocs = []
      action.payload.docs.forEach((d) => (!trackDocs.get(d._id) ? newDocs.push(d) : false))
      state.docs = [...state.docs, ...newDocs]
      state.loading = false
    })
    builder.addCase(fetchAllCampaignUserData.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.docs = []
      state.error = action.error.message
    })

    builder.addCase(fetchCampaignUserCoupons.pending, (state, action) => {})
    builder.addCase(fetchCampaignUserCoupons.fulfilled, (state, action) => {
      state.couponCodes = action.payload
      state.couponCodesLoading = false
    })
    builder.addCase(fetchCampaignUserCoupons.rejected, (state, action) => {
      state.couponCodesLoading = false
    })

    builder.addCase(fetchCampaignTypes.pending, (state, action) => {})
    builder.addCase(fetchCampaignTypes.fulfilled, (state, action) => {
      state.allCampaignTypes = action.payload.campaignTypes
    })
    builder.addCase(fetchCampaignTypes.rejected, (state, action) => {})
    builder.addCase(fetchProducts.pending, (state, action) => {})
    builder.addCase(fetchProducts.fulfilled, (state, action) => {
      state.productList = action.payload
    })
    builder.addCase(fetchProducts.rejected, (state, action) => {})
    builder.addCase(uploadFile.fulfilled, (state, action) => {})
    builder.addCase(uploadFile.rejected, (state, action) => {})
  },
})
export const {
  resetDocs,
  setLoading,
  setActivePage,
  setPageSize,
  setCouponCodesLoading,
  resetCouponCodes,
} = campaignUserDataSlice.actions
export default campaignUserDataSlice.reducer
